import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import BrandLogo from '../components/BrandLogo';
import { PRODUCT_DISPLAY_NAME } from '../config/product';
import { BrandEntrance, FadeIn, easeOut } from '../components/motion';
import api from '../config/api';
import './Auth.css';

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading, applyGoogleSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Complete OAuth redirect: /login?google=ok&token=...
  useEffect(() => {
    const google = searchParams.get('google');
    const token = searchParams.get('token');
    const message = searchParams.get('message');

    if (google === 'error') {
      const map = {
        not_authorized: 'Access denied. Your account is not authorized to use this app.',
        deactivated: 'Account is deactivated.',
        no_app_access: 'This account cannot sign in to the app.',
        no_email: 'Google did not provide an email address.',
        invalid_state: 'Sign-in expired. Please try again.',
        workspace_required:
          'Use your @gobunnyy.com Google Workspace account. Personal Gmail accounts cannot sign in.',
        email_unverified: 'Google has not verified this email address.',
      };
      setError(map[message] || message || 'Google sign-in failed.');
      navigate('/login', { replace: true });
      return;
    }

    if (google === 'ok' && token) {
      setLoading(true);
      applyGoogleSession(token)
        .then(() => navigate('/', { replace: true }))
        .catch((err) => {
          setError(err.message || 'Sign in failed');
          navigate('/login', { replace: true });
        })
        .finally(() => setLoading(false));
    }
  }, [searchParams, applyGoogleSession, navigate]);

  const startGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.get('/auth/google/start', {
        params: { return_origin: window.location.origin },
        headers: { Accept: 'application/json' },
      });
      if (!data?.authorize_url) {
        throw new Error('Google Sign-In is not configured on the server.');
      }
      window.location.href = data.authorize_url;
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          'Could not start Google Sign-In. Ensure GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET are set.'
      );
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-atmosphere" aria-hidden="true">
        <motion.span
          className="auth-orb auth-orb--primary"
          animate={
            reduceMotion
              ? undefined
              : {
                  x: [0, 28, -12, 0],
                  y: [0, -22, 14, 0],
                  scale: [1, 1.06, 0.97, 1],
                }
          }
          transition={
            reduceMotion
              ? undefined
              : { duration: 18, repeat: Infinity, ease: 'easeInOut' }
          }
        />
        <motion.span
          className="auth-orb auth-orb--secondary"
          animate={
            reduceMotion
              ? undefined
              : {
                  x: [0, -20, 16, 0],
                  y: [0, 18, -10, 0],
                  scale: [1, 0.94, 1.05, 1],
                }
          }
          transition={
            reduceMotion
              ? undefined
              : { duration: 22, repeat: Infinity, ease: 'easeInOut' }
          }
        />
        <span className="auth-grid" />
      </div>

      <div className="auth-theme-toggle">
        <ThemeToggle />
      </div>

      <div className="auth-hero">
        <BrandEntrance className="auth-brand">
          <BrandLogo variant="auth" />
        </BrandEntrance>

        <FadeIn delay={1} className="auth-card">
          <h2>Welcome back</h2>
          <p className="auth-subtitle">
            Sign in with your @gobunnyy.com Google Workspace account to open {PRODUCT_DISPLAY_NAME}.
          </p>
          {error && <div className="error">{error}</div>}
          <div className="google-signin-wrapper">
            <button
              type="button"
              className="btn btn-primary google-signin-btn"
              onClick={startGoogleSignIn}
              disabled={loading}
              style={{ width: '100%', minHeight: 44 }}
            >
              {loading ? 'Signing in…' : 'Sign in with Google Workspace'}
            </button>
          </div>
          {loading && <p className="auth-loading">Redirecting to Google…</p>}
        </FadeIn>

        <motion.p
          className="auth-footer"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 0.4, ease: easeOut }}
        >
          ERP, revenue, and procurement — one standard of excellence
        </motion.p>

        <motion.nav
          className="auth-legal"
          aria-label="Legal"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.4, ease: easeOut }}
        >
          <Link to="/about">About {PRODUCT_DISPLAY_NAME}</Link>
          <span className="auth-legal-sep" aria-hidden="true">
            ·
          </span>
          <a
            href="https://gobunnyy.com/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Use
          </a>
          <span className="auth-legal-sep" aria-hidden="true">
            ·
          </span>
          <a
            href="https://gobunnyy.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </motion.nav>
      </div>
    </div>
  );
};

export default Login;
